import { useState, useEffect, useMemo, Fragment } from 'react';
import { Text, Icon, Button, Popover, Tooltip, Dialog } from "@ui-component-library";
import { usePropsDataStore } from '../store/PropsDataStore';
import { useNavigationStore, pushMinSkogView } from '../store/NavigationStore';
import { useUserDataStore } from '../store/UserDataStore';
import { useDemoMinskogStore } from '../store/DemoMinskogStore';
import { DEMO_PROMPTS } from '../constants/demoPrompts';
import { ProfilePopover } from './MainNavigation/Overlays';
import { useScreenSize } from '../hooks/useScreenSize';
import PropertySelector from "@components/PropertySelector/PropertySelector";
import { cn } from '@utils/cn';
import {SetSystemAlertSeen, SetSystemAlertDismissed} from '@services/user';
import CaretIcon from '@assets/icons/caret-right.svg';

const AlertRow = ({title, seen, text, icon, onClick}) => {
    return <div className={`w-full rounded-lg bg-[#F5F5F5] px-4 py-2 gap-3 flex flex-row items-center cursor-pointer hover:bg-[#E6E6E6] ${seen ? 'opacity-80' : ''}`} onClick={onClick}>
        <Icon name={icon} size="large" />
        <div className="flex flex-col gap-1 flex-1 min-w-0">
            <Text variant="text-sm/leading-normal/medium">{title}</Text>
            <Text variant="text-xs/leading-normal/normal">{text}</Text>
        </div>
        {!seen && <div className="min-w-2 min-h-2 shrink-0 block bg-warning-500 rounded-full" aria-hidden />}
    </div>
}

export default function MinskogToolbar({onOpenMobileMenu, onTriggerKYC}) {
    const { props } = usePropsDataStore();
    const { view, setView } = useNavigationStore();
    const { userData } = useUserDataStore()
    const { isLarge } = useScreenSize();
    const { isDemo, DemoBlockFunctionWrapper} = useDemoMinskogStore();
    
    const [selectorTitle, setSelectorTitle] = useState('Välj fastighet');
    const [openProfilePopover, setOpenProfilePopover] = useState(false);
    const [openNotificationPopover, setOpenNotificationPopover] = useState(false);
    const [openPropertySelector, setOpenPropertySelector] = useState(props.length === 0);


    const [alertExpandedInfoBox, setAlertExpandedInfoBox] = useState(null);
    const [alerts, setAlerts] = useState([])

    const initial = Window?.user_data?.email ? Window?.user_data?.email.charAt(0).toUpperCase() : "";

    useEffect(() => {
        if(userData?.systemAlerts){
            setAlerts(userData.systemAlerts);
        }
    }, [userData?.systemAlerts, ])

    useEffect(() => {
        const handlePopState = (event) => {
            // When the user hits Back/Forward in the browser:
            // 1. Check if there is a state saved in history
            if (event.state && event.state.comp) {
                setView(event.state);
            } else {
                // 2. Fallback to URL or default if history state is empty
                const params = new URLSearchParams(window.location.search);
                const viewFromUrl = params.get('view');
                const next = { comp: viewFromUrl || 'overblick' };
                if (viewFromUrl === 'settings' && params.get('settings_tab')) {
                    next.settingsTab = params.get('settings_tab');
                }
                setView(next);
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [setView]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            let activeProps = props.filter(p => p?.active);

            if (activeProps.length === 0) {
                setSelectorTitle('Välj fastighet');
            } else if (activeProps.length === 1) {
                setSelectorTitle(
                    <span className='whitespace-nowrap overflow-hidden text-truncate capitalize'>
                        {activeProps[0].designation?.toLowerCase()}
                    </span>
                );
            } else {
                setSelectorTitle(`${activeProps.length} fastigheter`);
            }
        }, 0);

        return () => clearTimeout(timeout); 
    }, [props]);


    const handleSetSystemAlertAsSeen = async (id) => {
        const result = await SetSystemAlertSeen(id);
        if (result.ok) {
            setAlerts(prev => prev.map(alert => alert.id === id ? result.data : alert));
        }
    }

    const handleDismissAllAlerts = async () => {
        const alertIds = alerts.map(a => a.id);
        const results = await Promise.all(alertIds.map((id) => SetSystemAlertDismissed(id)));
        const dismissedIds = new Set(alertIds.filter((_, i) => results[i].ok));
        setAlerts(prev => prev.filter(alert => !dismissedIds.has(alert.id)));
    }

    const navigateTo = (compName, extra = {}) => {
        const current = useNavigationStore.getState().view;
        if (current?.comp === compName && !extra.settingsTab && !current?.settingsTab) return;
        if (current?.comp === compName && extra.settingsTab === current?.settingsTab) return;
        pushMinSkogView(compName, extra);
    };


    const ToolbarMenuButton = ({ label, iconName, isActive, onClick, isMinimized }) => {
        if (isMinimized) {
            return (
                <Tooltip key={label} content={label} side="right">
                    <Button
                        variant="tertiary"
                        className={cn('rounded-md p-1-5 bg-cloud-200 hover:bg-cloud-200', isActive && 'bg-cloud-200')}
                        size="icon"
                        aria-label={label}
                        onClick={onClick}
                    >
                        <Icon name={iconName} size="large" className={cn(isActive && "fill-[#1A1A1A]", label === "Logga ut" ? "rotate-90" : "")} />
                    </Button>
                </Tooltip>
            );
        }
        return (
            <div
                key={label}
                role="button"
                tabIndex={0}
                className="flex items-center px-4 py-1-5 rounded-md cursor-pointer transition-colors hover:bg-cloud-200 bg-cloud-200"
                onClick={onClick}
                onKeyDown={(e) => e.key === 'Enter' && onClick()}
            >
                <Icon name={iconName} size="large" className={cn(isActive && "fill-[#1A1A1A]", label === "Logga ut" ? "rotate-90" : "")} />
                <span className="ml-2">
                    <Text variant="text-sm/leading-normal/medium" className="text-charcoal-1000">{label}</Text>
                </span>
            </div>
        );
    };

    const profileLinks = useMemo(() => {
        const closeProfile = () => setOpenProfilePopover(false);
        const links = [];
        if (Window.user_data.isAdmin) {
            links.unshift({ id: 'admin', label: 'Admin', icon: 'UserCircleSimple', onClick: () => { closeProfile(); window.location.href = '/vb-admin/'; } });
        }
        if (!Window.user_data.isPremium) {
            links.push({ id: 'pricing', label: 'Uppgradera till Plus', icon: 'SparkleSimple', onClick: () => { closeProfile(); navigateTo('pricing'); } });
        }
        links.push(
            { id: 'settings', label: 'Inställningar', icon: 'GearSimple', onClick: () => { closeProfile(); navigateTo('settings'); } },
            { id: 'support', label: 'Support', icon: 'LifebuoySimple', onClick: () => { closeProfile(); window.location.href = Window.user_data.isPremium ? '/redirect-book-meeting/' : '/skoglig-radgivning/'; } },
            { id: 'my-ads', label: 'Mina virkesannonser', icon: 'ListStarSimple', onClick: () => { closeProfile(); navigateTo('settings', { settingsTab: 'my-ads' }); } },
            { id: 'logga-ut', label: 'Logga ut', icon: 'UploadSimple', onClick: () => { closeProfile(); window.location.href = '/logout/'; } },
        );
        return links;
    }, []);


    const notificationPopoverContent = () => {
        const handleAlertClick = async (itm) => {
            if (!itm.seen_at) {
                await handleSetSystemAlertAsSeen(itm.id);
            }

            switch(itm.action){
                case 'HREF':
                    window.location = itm.href || itm.action_data?.href;
                    break;
                case 'OPEN_MODAL':
                    setAlertExpandedInfoBox(itm.popup_content);
                    break;
                case 'OPEN_KYC':
                    onTriggerKYC();
                    break;
            }
            setOpenNotificationPopover(false);
        }

        return (
            <div className="w-[336px] flex flex-col overflow-hidden">
                <div className="flex justify-between items-center pb-2">
                    <Text variant="text-base/leading-normal/medium">Notiser</Text>
                    {alerts.length > 0 && (
                        <Button variant="tertiary" size="small" onClick={handleDismissAllAlerts}>
                            Rensa
                        </Button>
                    )}
                </div>

                <div className="overflow-y-auto flex flex-col gap-2">
                    {alerts.length === 0 ? (
                        <div className="relative w-full h-[176px] p-4 flex flex-col items-center justify-center gap-1">
                            <Icon name="BellSimple" className="rounded-md fill-[#737373]" size="xlarge" />
                            <Text variant="text-lg/leading-normal/semibold" className="text-[#737373] text-center">Inga notiser</Text>
                            <Text variant="text-sm/leading-normal/normal" className="text-[#737373] text-center">Du har inga notiser att visa.</Text>
                        </div>
                    ) : (
                        alerts.map((itm) => <AlertRow key={itm.id} seen={!!itm.seen_at} title={itm.title} icon={itm.icon} text={itm.text} onClick={() => {handleAlertClick(itm)}} />)
                    )}
                </div>
            </div>

        )

    }

    const selectorTitleDemoAdjusted = isDemo ? "Exempelfastighet" : selectorTitle;

    return(
        <>
            {/* z-index is here to make sure popover for alerts doens't go behind karta. But also so that the mobile navigation still goes on top of this */}
             <div className={cn('flex justify-between w-full shrink-0 z-[var(--z-toolbar)]', isLarge? 'h-[68px] p-4 bg-[#FBFBFB]':'h-14 py-2 px-4 bg-cloud-50 border-b-[1px] border-solid border-[#E6E6E6]', )}>
                               
                {isLarge ? (
                    <div className='flex flex-row gap-2'>
                        <div className="cursor-pointer bg-cloud-200 rounded-md p-2" onClick={() => { window.history.back(); }} aria-label="Gå bakåt">
                            <CaretIcon className="rotate-180" height="20px" width="20px" />
                        </div>

                        <div className="cursor-pointer bg-cloud-200 rounded-md p-2" onClick={() => { window.history.forward(); }} aria-label="Gå framåt">
                            <CaretIcon height="20px" width="20px"/>
                        </div>
                    </div>
                ) : (
                    <div className='flex flex-row items-center gap-2 min-w-0'>
                        <Button variant="tertiary" className="bg-cloud-200 shrink-0 rounded-md p-1-5" size="icon" onClick={onOpenMobileMenu} aria-label="Öppna meny">
                            <Icon name="SidebarSimple" size="large" />
                        </Button>
                        <Button
                            onClick={() => DemoBlockFunctionWrapper(() => setOpenPropertySelector(true), DEMO_PROMPTS.ADD_PROPERTIES)}
                            variant="secondary"
                            className="flex flex-row shrink-0 max-w-[174px]"
                            size="small"
                        >
                            <span className="truncate overflow-hidden ">{selectorTitleDemoAdjusted}</span>
                            <Icon name="ChevronDownSimple" size="medium" />
                        </Button>
                    </div>
                )}
                {isLarge && (
                    <Button 
                        onClick={() => {DemoBlockFunctionWrapper(() => setOpenPropertySelector(true), DEMO_PROMPTS.ADD_PROPERTIES)}} 
                        variant="secondary" 
                        className="flex flex-row shrink-0 max-w-[190px]"
                    >
                        <span className="truncate overflow-hidden ">{selectorTitleDemoAdjusted}</span>
                        <Icon name="ChevronDownSimple" size="medium" />
                    </Button>
                    
                )}
                
                 <div className='flex flex-row items-center gap-1-5'>
                    <ToolbarMenuButton  
                        key={'karta'} 
                        label={'Kartvy'}
                        iconName={view?.comp === 'karta' ? 'MapTrifoldFilled' : 'MapTrifoldSimple'}
                        isActive={view?.comp === 'karta'}
                        onClick={() => view?.comp === 'karta' ? navigateTo('overblick') : navigateTo('karta')}
                        isMinimized={!isLarge}
                    />

                    <Popover
                        content={notificationPopoverContent()}
                        side="bottom"
                        align="end"
                        open={openNotificationPopover}
                        onOpenChange={(open) => {
                            setOpenNotificationPopover(open);
                            if (open) {
                                setOpenProfilePopover(false);
                            }
                        }}
                        contentClassName="p-3 overflow-hidden max-h-[500px] flex flex-col"
                    >
                        <Button variant="tertiary" className="bg-cloud-200 rounded-md p-1-5 relative overflow-visible" size="icon" aria-label="Visa aviseringar">
                            <Icon name="BellSimple" size="large"/>
                            {alerts.filter(itm => !itm.seen_at).length > 0 && (
                                <Text variant="text-xs/leading-none/black" className="absolute top-0 right-0 px-2 py-1 text-white-50 rounded-full bg-danger-500 translate-x-1/3 -translate-y-1/3">{alerts.filter(itm => !itm.seen_at).length}</Text>
                            )}
                        </Button>
                    </Popover>

                    <ProfilePopover
                        initial={initial}
                        profileLinks={profileLinks}
                        view={view}
                        userData={Window.user_data}
                        open={openProfilePopover}
                        onOpenChange={(open) => {
                            DemoBlockFunctionWrapper(() => setOpenProfilePopover(open), DEMO_PROMPTS.MANAGE_PROFILE);
                            if (open) setOpenNotificationPopover(false);
                        }}
                        side="bottom"
                        aligh="end"
                    >
                        <Button variant="tertiary" size="icon" className={`rounded-md p-1`}  aria-label="Öppna profilmeny">
                            <Text variant="text-xs/leading-normal/medium" className={cn( 'rounded-2xl p-3 w-5 h-5 flex items-center justify-center',openProfilePopover? 'text-white-50 bg-[#6B21A8]' : 'text-charcoal-1000')}>{initial}</Text>
                        </Button>
                    </ProfilePopover>

                 </div>

            </div>
            <PropertySelector isOpen={openPropertySelector} onClose={() => setOpenPropertySelector(false)} />
            <Dialog isOpen={alertExpandedInfoBox}>
                <Dialog.Header className="justify-between items-center">
                    <Text variant="text-lg/leading-normal/medium">
                        {alertExpandedInfoBox?.title}
                    </Text>
                </Dialog.Header>
                <Dialog.Content className="whitespace-pre-line">
                    <Text variant="text-base/leading-normal/normal">{alertExpandedInfoBox?.text}</Text>
                </Dialog.Content>
                <Dialog.Footer>
                    
                    <Button variant="primary" onClick={() => { window.location.href = alertExpandedInfoBox?.href; setAlertExpandedInfoBox(false) }}><>
                        {alertExpandedInfoBox?.nextText}
                        <Icon
                            color={"white-500"}
                            name="ArrowRightSimple"
                        />
                    </></Button>
                    <Button variant="secondary" onClick={() => setAlertExpandedInfoBox(false)}>Stäng</Button>
                </Dialog.Footer>
            </Dialog>
        </>
    )

}